/**
 * Expense Trends Chart Component
 *
 * Combined pie chart for category breakdown and line chart for time series.
 */

import React from 'react';
import { Card, CardContent, Typography, Box, Skeleton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Doughnut, Line } from 'react-chartjs-2';
import type { ExpenseTrendsData } from '../../../types/financialInsights';
import { getChartColors } from '../../../utils/chartHelpers';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

interface ExpenseTrendsChartProps {
  data: ExpenseTrendsData | undefined;
  loading: boolean;
}

export const ExpenseTrendsChart: React.FC<ExpenseTrendsChartProps> = ({
  data,
  loading,
}) => {
  const theme = useTheme();

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="rectangular" width="100%" height={400} />
        </CardContent>
      </Card>
    );
  }

  if (
    !data ||
    (data.categories.length === 0 && data.time_series.length === 0)
  ) {
    return (
      <Card>
        <CardContent>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ height: 400 }}
          >
            <Typography variant="h6" color="text.secondary">
              No Expense Data Available
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const pieData = {
    labels: data.categories.map(c => c.category),
    datasets: [
      {
        data: data.categories.map(c => c.value),
        backgroundColor: getChartColors(data.categories.length),
        borderWidth: 2,
      },
    ],
  };

  const lineData = {
    labels: data.time_series.map(t => t.date),
    datasets: [
      {
        label: 'Expenses',
        data: data.time_series.map(t => t.value),
        borderColor: theme.palette.primary.main,
        backgroundColor: `${theme.palette.primary.main}33`,
        tension: 0.4,
      },
    ],
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Expense Trends
        </Typography>
        <Box display="flex" gap={3} flexDirection={{ xs: 'column', md: 'row' }}>
          <Box sx={{ flex: 1, height: 300 }}>
            <Doughnut
              data={pieData}
              options={{ responsive: true, maintainAspectRatio: false }}
            />
          </Box>
          <Box sx={{ flex: 1, height: 300 }}>
            <Line
              data={lineData}
              options={{ responsive: true, maintainAspectRatio: false }}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ExpenseTrendsChart;
